import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  HardDrive, 
  Upload, 
  FileText, 
  Database, 
  Activity, 
  Users, 
  BarChart3,
  FolderOpen,
  Shield,
  RefreshCw,
  ChevronRight,
  Info,
  Target,
  TrendingUp,
  Smartphone,
  Monitor,
  AlertTriangle
} from 'lucide-react';

export const DriveHelpPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      id: 'header',
      title: 'Header e Navigazione',
      description: 'La sezione superiore della pagina offre accesso rapido alle funzioni principali.',
      details: [
        'Pulsante "Indietro" per tornare alla pagina precedente',
        'Titolo della pagina con descrizione',
        'Indicatore di permessi per admin/moderatori',
        'Selettore utente per visualizzare file di altri utenti',
        'Pulsante "Aggiorna" per ricaricare i file',
        'Pulsante "Carica File" per aprire l\'area di upload'
      ],
      icon: <HardDrive className="h-5 w-5" />
    },
    {
      id: 'stats',
      title: 'Statistiche Rapide',
      description: 'Quattro card che mostrano un riepilogo immediato dello spazio di archiviazione.',
      details: [
        '**File Totali**: Numero complessivo di file caricati',
        '**Spazio Usato**: Quantità di spazio occupato dai file',
        '**Utilizzato**: Percentuale di spazio utilizzato rispetto al limite',
        '**Utenti**: Numero di utenti (solo per admin/moderatori)'
      ],
      icon: <TrendingUp className="h-5 w-5" />
    },
    {
      id: 'progress-bar',
      title: 'Barra di Progresso Spazio',
      description: 'Visualizzazione grafica dello spazio utilizzato con indicatori colorati.',
      details: [
        '**Barra di progresso**: Mostra la percentuale di spazio utilizzato',
        '**Colori dinamici**: Verde (normale), Giallo (75%+), Rosso (90%+)',
        '**Limite massimo**: 100MB di spazio totale disponibile',
        '**Contatore dettagliato**: File count e percentuale esatta'
      ],
      icon: <BarChart3 className="h-5 w-5" />
    },
    {
      id: 'upload-area',
      title: 'Area di Upload',
      description: 'Sezione dedicata al caricamento di nuovi file nel drive.',
      details: [
        '**Drag & Drop**: Trascina i file direttamente nell\'area',
        '**Selezione file**: Clicca per aprire il selettore file',
        '**Limiti**: Massimo 10MB per file, 100MB totali',
        '**Tipi supportati**: Documenti, immagini, PDF, ecc.',
        '**Upload per altri**: Admin/moderatori possono caricare per altri utenti'
      ],
      icon: <Upload className="h-5 w-5" />
    },
    {
      id: 'file-list',
      title: 'Lista File',
      description: 'Visualizzazione organizzata di tutti i file caricati.',
      details: [
        '**Nome file**: Visualizzazione chiara del nome del file',
        '**Dimensione**: Dimensione in formato leggibile (KB, MB)',
        '**Data caricamento**: Quando il file è stato caricato',
        '**Azioni**: Scarica, elimina, visualizza dettagli',
        '**Filtri**: Ricerca e ordinamento dei file'
      ],
      icon: <FolderOpen className="h-5 w-5" />
    },
    {
      id: 'permissions',
      title: 'Sistema di Permessi',
      description: 'Gestione degli accessi basata sui ruoli utente.',
      details: [
        '**Utenti normali**: Visualizzano solo i propri file',
        '**Moderatori**: Possono vedere file di tutti gli utenti',
        '**Admin**: Accesso completo a tutti i file e funzioni',
        '**Selettore utente**: Cambia la visualizzazione per utenti specifici',
        '**Indicatore permessi**: Mostra il ruolo attuale'
      ],
      icon: <Shield className="h-5 w-5" />
    },
    {
      id: 'mobile-features',
      title: 'Funzionalità Mobile',
      description: 'Caratteristiche specifiche per dispositivi mobili.',
      details: [
        '**Layout ottimizzato**: Statistiche in una riga, controlli compatti',
        '**Upload nascosto**: L\'area upload nasconde le statistiche',
        '**Touch-friendly**: Pulsanti grandi e facili da premere',
        '**Responsive**: Si adatta automaticamente alla schermata',
        '**Gestione spazio**: Ottimizzazione per schermi piccoli'
      ],
      icon: <Smartphone className="h-5 w-5" />
    },
    {
      id: 'desktop-features',
      title: 'Funzionalità Desktop',
      description: 'Caratteristiche specifiche per schermi grandi.',
      details: [
        '**Layout completo**: Tutte le sezioni visibili contemporaneamente',
        '**Upload sempre visibile**: Area upload non nasconde altre sezioni',
        '**Navigazione avanzata**: Controlli più grandi e dettagliati',
        '**Hover effects**: Interazioni avanzate con mouse',
        '**Gestione utenti**: Selettore utente completo per admin/moderatori'
      ],
      icon: <Monitor className="h-5 w-5" />
    }
  ];

  const tips = [
    'Usa il pulsante "Aggiorna" se i file non si aggiornano automaticamente',
    'La barra di progresso cambia colore quando lo spazio si esaurisce',
    'Puoi trascinare i file direttamente nell\'area di upload',
    'I file vengono organizzati per data di caricamento',
    'Admin e moderatori possono vedere file di tutti gli utenti',
    'Il limite di 100MB è condiviso tra tutti gli utenti',
    'Usa il selettore utente per cambiare la visualizzazione',
    'I file eliminati non possono essere recuperati'
  ];

  const workflow = [
    {
      step: 1,
      title: 'Accedi al Drive',
      description: 'Clicca su "Drive" nella sidebar per accedere'
    },
    {
      step: 2,
      title: 'Controlla lo Spazio',
      description: 'Verifica le statistiche e la barra di progresso'
    },
    {
      step: 3,
      title: 'Carica File',
      description: 'Clicca su "Carica File" e seleziona i documenti'
    },
    {
      step: 4,
      title: 'Gestisci File',
      description: 'Visualizza, scarica o elimina i file caricati'
    },
    {
      step: 5,
      title: 'Monitora l\'Uso',
      description: 'Controlla regolarmente lo spazio utilizzato'
    }
  ];

  const fileTypes = [
    {
      type: 'Documenti',
      icon: '📄',
      description: 'PDF, DOC, DOCX, TXT, RTF',
      examples: 'CV, lettere di presentazione, certificati'
    },
    {
      type: 'Immagini',
      icon: '🖼️',
      description: 'JPG, PNG, GIF, SVG',
      examples: 'Foto profilo, screenshot, grafici'
    },
    {
      type: 'Archivi',
      icon: '📦',
      description: 'ZIP, RAR, 7Z',
      examples: 'Portfolio, progetti compressi'
    },
    {
      type: 'Altri',
      icon: '📎',
      description: 'Altri formati supportati',
      examples: 'Presentazioni, fogli di calcolo'
    }
  ];

  const limits = [
    {
      limit: 'Dimensione singolo file',
      value: '10 MB',
      description: 'Massimo per ogni file caricato'
    },
    {
      limit: 'Spazio totale',
      value: '100 MB',
      description: 'Limite condiviso tra tutti gli utenti'
    },
    {
      limit: 'File simultanei',
      value: 'Illimitato',
      description: 'Numero di file che puoi caricare'
    },
    {
      limit: 'Tipi supportati',
      value: 'Tutti',
      description: 'Formati di file standard'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-4 lg:space-y-6">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate('/help')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl">
                <HardDrive className="h-6 w-6 text-orange-700" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                  Guida Drive File
                </h1>
                <p className="text-sm text-gray-600">
                  Scopri come gestire efficacemente i tuoi file personali
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Panoramica */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Info className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">
                Panoramica
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Il Drive è il tuo spazio di archiviazione personale per tutti i file importanti. 
                Qui puoi caricare, organizzare e gestire documenti, immagini e altri file necessari 
                per la tua ricerca di lavoro. Il sistema è progettato per essere sicuro, efficiente 
                e facile da usare, con un limite di 100MB condiviso tra tutti gli utenti.
              </p>
            </div>
          </div>
        </div>

        {/* Funzionalità */}
        <div className="space-y-4 lg:space-y-6">
          {features.map((feature, index) => (
            <div key={feature.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-orange-100 rounded-lg">
                  {feature.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                        <ChevronRight className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span dangerouslySetInnerHTML={{ __html: detail }} />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Limiti e Restrizioni */}
        <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-2xl border border-red-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Limiti e Restrizioni
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {limits.map((limit, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-red-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-red-900">{limit.limit}</span>
                  <span className="text-lg font-bold text-red-700">{limit.value}</span>
                </div>
                <p className="text-sm text-red-800">{limit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Tipi di File Supportati */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Tipi di File Supportati
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fileTypes.map((type, index) => (
              <div key={index} className="bg-white rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{type.icon}</span>
                  <span className="font-semibold text-blue-900">{type.type}</span>
                </div>
                <p className="text-sm text-blue-800 mb-2">{type.description}</p>
                <p className="text-xs text-blue-700 italic">{type.examples}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5" />
            Workflow Consigliato
          </h3>
          <div className="space-y-3">
            {workflow.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                  {step.step}
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 text-sm">{step.title}</h4>
                  <p className="text-green-800 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Suggerimenti */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl border border-purple-200 p-4 lg:p-6">
          <h3 className="text-lg font-semibold text-purple-900 mb-4 flex items-center gap-2">
            <Info className="h-5 w-5" />
            Suggerimenti Utili
          </h3>
          <ul className="space-y-2">
            {tips.map((tip, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-purple-800">
                <div className="w-1.5 h-1.5 bg-purple-600 rounded-full mt-2 flex-shrink-0"></div>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Azioni */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Pronto a gestire i tuoi file?
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/drive')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                <HardDrive className="h-4 w-4" />
                Vai al Drive
              </button>
              <button
                onClick={() => navigate('/drive')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Upload className="h-4 w-4" />
                Carica File
              </button>
              <button
                onClick={() => navigate('/help')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                <FileText className="h-4 w-4" />
                Altre Guide
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 